'use client';
import React from 'react';
import type { Earnings } from '@benzinga/calendar-manager';
import { formatPercentage } from '@benzinga/utils/numberUtils';
import { useBenzingaEdge } from '@benzinga/edge';
import {
  QuoteDataTable,
  QuoteHeaderRow,
  QuoteSectionRow,
  QuoteTd,
  QuoteTh,
  QuoteThead,
} from './QuoteDataTable';
import { EdgeGateCTAOverlay } from './EdgeRowsGate';

const fmtEps = (v: string | null | undefined): string => {
  if (!v || v === '0') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : `$${n.toFixed(2)}`;
};

const fmtRev = (v: string | null | undefined): string => {
  if (!v || v === '0') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${n.toFixed(0)}`;
};

const SurpriseCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  if (!value || value === '0') return <QuoteTd align="right" muted>—</QuoteTd>;
  const n = parseFloat(value);
  const text = `${n > 0 ? '+' : ''}${formatPercentage(n * 100)}%`;
  const tone = n > 0 ? 'text-green-600' : n < 0 ? 'text-red-600' : 'text-gray-500';
  return (
    <QuoteTd align="right" className={`font-medium ${tone}`}>
      {text}
    </QuoteTd>
  );
};

const periodLabel = (e: Earnings): string => {
  const yr = e.period_year ? `'${String(e.period_year).slice(-2)}` : '';
  return e.period === 'FY' ? `FY ${yr}` : `${e.period} ${yr}`;
};

interface RecentEarningsResultsTableProps {
  earnings: Earnings[];
}

const RecentEarningsResultsTable: React.FC<RecentEarningsResultsTableProps> = ({ earnings }) => {
  const quarterly = earnings.filter(e => e.period !== 'FY').slice(0, 4);
  const annual = earnings.filter(e => e.period === 'FY').slice(0, 2);
  const cols: { label: string; e: Earnings }[] = [
    ...quarterly.map(e => ({ label: periodLabel(e), e })),
    ...annual.map(e => ({ label: periodLabel(e), e })),
  ];

  if (cols.length === 0) return null;

  return (
    <QuoteDataTable>
      <QuoteThead>
        <QuoteHeaderRow>
          <QuoteTh>Metric</QuoteTh>
          {cols.map((col, index) => (
            <QuoteTh key={`${col.label}-${index}`} align="right">
              {col.label}
            </QuoteTh>
          ))}
        </QuoteHeaderRow>
      </QuoteThead>
      <tbody>
        <QuoteSectionRow colSpan={cols.length + 1}>Earnings Per Share (Non-GAAP)</QuoteSectionRow>
        <tr className="border-b border-gray-100">
          <QuoteTd muted>
            Estimate
          </QuoteTd>
          {cols.map((col, index) => (
            <QuoteTd key={`${col.label}-${index}`} align="right">
              {fmtEps(col.e.eps_est)}
            </QuoteTd>
          ))}
        </tr>
        <tr className="border-b border-gray-100 bg-gray-50">
          <QuoteTd bold>
            Actual
          </QuoteTd>
          {cols.map((col, index) => (
            <QuoteTd key={`${col.label}-${index}`} align="right" bold>
              {fmtEps(col.e.eps)}
            </QuoteTd>
          ))}
        </tr>
        <tr className="border-b border-gray-200">
          <QuoteTd muted>
            Surprise
          </QuoteTd>
          {cols.map((col, index) => (
            <SurpriseCell key={`${col.label}-${index}`} value={col.e.eps_surprise_percent} />
          ))}
        </tr>

        <QuoteSectionRow colSpan={cols.length + 1}>Revenue</QuoteSectionRow>
        <tr className="border-b border-gray-100">
          <QuoteTd muted>
            Estimate
          </QuoteTd>
          {cols.map((col, index) => (
            <QuoteTd key={`${col.label}-${index}`} align="right">
              {fmtRev(col.e.revenue_est)}
            </QuoteTd>
          ))}
        </tr>
        <tr className="border-b border-gray-100 bg-gray-50">
          <QuoteTd bold>
            Actual
          </QuoteTd>
          {cols.map((col, index) => (
            <QuoteTd key={`${col.label}-${index}`} align="right" bold>
              {fmtRev(col.e.revenue)}
            </QuoteTd>
          ))}
        </tr>
        <tr>
          <QuoteTd muted>
            Surprise
          </QuoteTd>
          {cols.map((col, index) => (
            <SurpriseCell key={`${col.label}-${index}`} value={col.e.revenue_surprise_percent} />
          ))}
        </tr>
      </tbody>
    </QuoteDataTable>
  );
};

export default RecentEarningsResultsTable;

interface ForwardEstimatesTableProps {
  forwardEarnings: Earnings[];
}

export const ForwardEstimatesTable: React.FC<ForwardEstimatesTableProps> = ({ forwardEarnings }) => {
  const now = new Date();
  const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const rows = forwardEarnings
    .filter(
      e =>
        ((e.eps_est && e.eps_est !== '0') || (e.revenue_est && e.revenue_est !== '0')) &&
        !!e.date &&
        e.date.slice(0, 10) > todayISO,
    )
    .slice()
    .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''))
    .slice(0, 6);
  const { hasEdgeSubscription } = useBenzingaEdge();
  const isGated = !hasEdgeSubscription;
  const hasOverlay = isGated && rows.length > 1;
  if (rows.length === 0) return null;

  const colBlurred = (index: number) => (isGated && index > 0 ? 'blur-sm opacity-60 select-none' : '');

  return (
    <div className="relative">
      <div className="overflow-x-auto rounded border border-[#e9ecef] overflow-visible">
        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Analyst Estimates — Forward Periods
          </span>
          <span className="text-xs text-gray-400">Source: Wall Street consensus</span>
        </div>
        <table className="w-full min-w-max text-sm">
          <QuoteThead>
            <tr className="border-b border-gray-100">
              <QuoteTh>Metric</QuoteTh>
              {rows.map((row, index) => (
                <QuoteTh
                  key={row.id}
                  align="right"
                  className={colBlurred(index)}
                  aria-hidden={index > 0 && isGated || undefined}
                >
                  {periodLabel(row)}E
                </QuoteTh>
              ))}
            </tr>
          </QuoteThead>
          <tbody>
            <QuoteSectionRow colSpan={rows.length + 1}>Earnings Per Share (Non-GAAP)</QuoteSectionRow>
            <tr className="border-b border-gray-100">
              <QuoteTd muted>
                Consensus
              </QuoteTd>
              {rows.map((row, index) => (
                <QuoteTd
                  key={row.id}
                  align="right"
                  bold
                  className={colBlurred(index)}
                  aria-hidden={index > 0 && isGated || undefined}
                >
                  {fmtEps(row.eps_est)}
                </QuoteTd>
              ))}
            </tr>

            <QuoteSectionRow colSpan={rows.length + 1}>Revenue</QuoteSectionRow>
            <tr>
              <QuoteTd muted>
                Consensus
              </QuoteTd>
              {rows.map((row, index) => (
                <QuoteTd
                  key={row.id}
                  align="right"
                  bold
                  className={colBlurred(index)}
                  aria-hidden={index > 0 && isGated || undefined}
                >
                  {fmtRev(row.revenue_est)}
                </QuoteTd>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {hasOverlay && <EdgeGateCTAOverlay type="forward-estimates" />}
    </div>
  );
};
